import { useMutation, useQueryClient } from '@tanstack/react-query';

import { moveTask as moveTaskRequest } from '@/lib/api/generated/api';
import type { TaskView, WorkspaceAggregate } from '@/lib/api/generated/model';
import { workspaceKeys } from '@/features/workspaces';
import { classifyMoveFailure, type MoveRefusal } from '../errors';
import { useTriggerRefreshStore } from '../triggerRefreshStore';
import { TASK_MUTATION_KEYS } from './taskMutationKeys';

export interface MoveTaskInput {
  workspaceId: string;
  taskId: string;
  targetStepId: string;
  expectedVersion: number;
  /** Why, when a reader would want to know later — "Undo" (spec 098). */
  reason?: string;
  /** Outlive the page: an action held until it goes away (spec 154 R3). */
  keepalive?: boolean;
}

interface MutationContext {
  previousAggregate: WorkspaceAggregate | null | undefined;
}

export function useMoveTask() {
  const queryClient = useQueryClient();

  return useMutation<
    { task: TaskView; refusal: null } | { task: null; refusal: MoveRefusal },
    Error,
    MoveTaskInput,
    MutationContext
  >({
    mutationKey: TASK_MUTATION_KEYS.move,
    mutationFn: async ({
      workspaceId,
      taskId,
      targetStepId,
      expectedVersion,
      reason,
      keepalive,
    }) => {
      const response = await moveTaskRequest(
        workspaceId,
        taskId,
        {
          target_step_id: targetStepId,
          expected_version: expectedVersion,
          reason: reason ?? null,
        },
        keepalive ? { keepalive } : undefined,
      );
      if (response.status === 200) {
        return { task: response.data.task, refusal: null };
      }
      return {
        task: null,
        refusal: classifyMoveFailure(response.status, response.data),
      };
    },
    onMutate: async ({ workspaceId, taskId, targetStepId }) => {
      await queryClient.cancelQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
      const previousAggregate =
        queryClient.getQueryData<WorkspaceAggregate | null>(
          workspaceKeys.detail(workspaceId),
        );

      queryClient.setQueryData(
        workspaceKeys.detail(workspaceId),
        (prev: WorkspaceAggregate | null | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: prev.tasks.map((task) =>
              task.id === taskId ? { ...task, position: targetStepId } : task,
            ),
          };
        },
      );

      return { previousAggregate };
    },
    onSuccess: (result, { workspaceId }, context) => {
      if (result.refusal) {
        // A refusal is a *resolved* mutation (a 409/422 body), not a thrown
        // error, so it does not reach onError — restore the optimistic
        // write's snapshot here instead (FR-028, FR-030). A stale refusal
        // additionally needs the whole board refetched against the true
        // state (FR-029); invalidating after restoring the snapshot covers
        // that without racing the restore.
        if (context) {
          queryClient.setQueryData(
            workspaceKeys.detail(workspaceId),
            context.previousAggregate,
          );
        }
        void queryClient.invalidateQueries({
          queryKey: workspaceKeys.detail(workspaceId),
        });
        return;
      }

      // The optimistic write in onMutate only set `position`, not `version`
      // (the server assigns that). Without this, the cached task keeps the
      // pre-move version forever, and the next move on the same task sends
      // that stale `expectedVersion` — the server then refuses it as a
      // version conflict even though nothing else changed it. Replace the
      // cached task with the server's authoritative copy so the next move
      // carries the right version.
      queryClient.setQueryData(
        workspaceKeys.detail(workspaceId),
        (prev: WorkspaceAggregate | null | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: prev.tasks.map((task) =>
              task.id === result.task.id ? result.task : task,
            ),
          };
        },
      );

      // A successful move may have fired a backend trigger that keeps
      // writing to this task or workspace (another move, a comment) outside
      // of this response. Open a short polling window so those follow-on
      // writes show up without a manual refresh.
      useTriggerRefreshStore.getState().armWindow(workspaceId);

      // The window alone only arms *future* polling — the cached aggregate
      // above is still fresh (staleTime), so nothing refetches until the
      // first 2s tick. Invalidating here forces that first refetch right
      // away, rather than making a trigger's follow-on write wait out a
      // polling interval it had no way to know about.
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
    },
    onError: (_error, { workspaceId }, context) => {
      if (context) {
        queryClient.setQueryData(
          workspaceKeys.detail(workspaceId),
          context.previousAggregate,
        );
      }
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
    },
  });
}
